import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Recording a short clip on the phone. The clip goes to the person's own computer inside the encrypted link and is turned into text
 * there (ADR 0026); nothing here uploads anywhere else, and the clip is dropped as soon as it has been sent.
 */

export const MAX_SECONDS = 60;
/** A low bitrate is plenty for speech and keeps a full minute well under what one relay message may carry. */
const BITS_PER_SECOND = 32_000;
/** Refuse a clip bigger than this (a minute at the rate above is about 240 KB; a relay message may carry about 1 MB, and base64 adds a third). */
export const MAX_CLIP_BYTES = 600_000;

/** Best-first. Chrome and Android make WebM/Opus; iPhone Safari makes MP4/AAC. */
const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

export function pickMime(isSupported: (mime: string) => boolean): string | null {
  return MIMES.find((m) => isSupported(m)) ?? null;
}

/** Can this browser record at all (needs a secure page, the microphone API and MediaRecorder)? */
export function canRecord(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== "undefined";
}

/** A sentence for each way the microphone can refuse, saying what to do. */
export function micErrorMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") return "Microphone access is off for Brocoded. Allow it in your phone's settings, then try again.";
  if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") return "No microphone was found on this phone.";
  if (name === "NotReadableError" || name === "AbortError" || name === "TrackStartError") return "Another app is using the microphone. Close it and try again.";
  return "Couldn't start the microphone. Try again.";
}

/** The command in a "run: …" sentence (the computer words its own instruction), so it can be shown on its own line. */
export function splitReason(reason: string): { lead: string; command: string | null } {
  const m = /^(.*?)(?:run|Run):\s*(.+)$/s.exec(reason.trim());
  if (!m) return { lead: reason.trim(), command: null };
  const lead = m[1]!.trim().replace(/\s*On your computer,?$/i, "").trim().replace(/[,:]$/, "");
  return { lead: /[.!?]$/.test(lead) ? lead : `${lead}.`, command: m[2]!.trim() };
}

export interface Clip {
  bytes: Uint8Array;
  /** What the recorder made, with codec details removed ("audio/webm"). */
  mime: string;
  seconds: number;
}

export type RecorderPhase = "idle" | "starting" | "recording";

/**
 * Tap to start, tap to stop. `stop()` resolves with the clip; reaching `MAX_SECONDS` stops it by itself and hands the clip to
 * `onLimit`. The microphone is released the moment recording ends.
 */
/** Bars in the live level meter (about 2.5 seconds of sound at 80 ms each). */
export const LEVEL_BARS = 32;

/** Nothing but silence in the meter for this long: say the phone can't hear anything. */
export const quietMeter = (levels: number[]) => Math.max(...levels) < 0.06;

export function useRecorder(opts: { onLimit(clip: Clip): void }) {
  const [phase, setPhase] = useState<RecorderPhase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [levels, setLevels] = useState<number[]>(() => Array(LEVEL_BARS).fill(0));
  const rec = useRef<{ recorder: MediaRecorder; stream: MediaStream; chunks: Blob[]; startedAt: number; mime: string; timer: ReturnType<typeof setInterval>; meter?: ReturnType<typeof setInterval>; ctx?: AudioContext } | null>(null);
  const onLimit = useRef(opts.onLimit);
  onLimit.current = opts.onLimit;

  const release = useCallback(() => {
    const r = rec.current;
    if (!r) return;
    clearInterval(r.timer);
    if (r.meter) clearInterval(r.meter);
    void r.ctx?.close?.().catch(() => {});
    r.stream.getTracks().forEach((t) => t.stop());
    rec.current = null;
    setPhase("idle");
    setSeconds(0);
    setLevels(Array(LEVEL_BARS).fill(0));
  }, []);

  const finish = useCallback((): Promise<Clip | null> => {
    const r = rec.current;
    if (!r) return Promise.resolve(null);
    return new Promise((resolve) => {
      const seconds = Math.max(1, Math.round((Date.now() - r.startedAt) / 1000));
      r.recorder.onstop = async () => {
        const blob = new Blob(r.chunks, { type: r.mime });
        release();
        const bytes = new Uint8Array(await blob.arrayBuffer());
        resolve({ bytes, mime: r.mime.split(";")[0]!, seconds });
      };
      if (r.recorder.state !== "inactive") r.recorder.stop();
      else r.recorder.onstop?.(new Event("stop"));
    });
  }, [release]);

  const start = useCallback(async (): Promise<void> => {
    if (rec.current) return;
    if (!canRecord()) throw new Error("This browser can't record audio. Use your keyboard's microphone instead.");
    setPhase("starting");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    } catch (e) {
      setPhase("idle");
      throw new Error(micErrorMessage(e));
    }
    const mime = pickMime((m) => MediaRecorder.isTypeSupported(m));
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: BITS_PER_SECOND });
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      setPhase("idle");
      throw new Error("This browser can't record audio. Use your keyboard's microphone instead.");
    }
    const chunks: Blob[] = [];
    recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
    const startedAt = Date.now();
    const timer = setInterval(() => {
      const s = Math.floor((Date.now() - startedAt) / 1000);
      setSeconds(s);
      if (s >= MAX_SECONDS) {
        clearInterval(timer); // once: finishing takes a moment and must not be started twice
        void finish().then((clip) => clip && onLimit.current(clip));
      }
    }, 250);
    rec.current = { recorder, stream, chunks, startedAt, mime: recorder.mimeType || mime || "audio/webm", timer };
    // A live level meter, so the person can see the phone is hearing them (best effort: a browser without it just shows the timer).
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        void ctx.resume?.();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        rec.current.ctx = ctx;
        rec.current.meter = setInterval(() => {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) sum += ((v - 128) / 128) ** 2;
          const level = Math.min(1, Math.sqrt(sum / buf.length) * 4); // speech sits around 0.05 to 0.25 before the boost
          setLevels((l) => [...l.slice(1), level]);
        }, 80);
      }
    } catch {
      /* no meter */
    }
    recorder.start(1000);
    setSeconds(0);
    setPhase("recording");
  }, [finish]);

  const cancel = useCallback(() => {
    const r = rec.current;
    if (!r) return;
    r.recorder.onstop = null;
    if (r.recorder.state !== "inactive") r.recorder.stop();
    release();
  }, [release]);

  useEffect(() => cancel, [cancel]); // leaving the screen releases the microphone

  return { phase, seconds, levels, start, stop: finish, cancel };
}

/** Float samples (-1..1) as 16-bit integers, clipped. */
export function floatToPcm16(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]!));
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
  }
  return out;
}

/**
 * A recording turned into 16 kHz mono samples on the phone (the browser decodes whatever its recorder made), so the computer needs
 * no audio converter. Null when this browser can't (then the recording is sent as it is).
 */
export async function clipToPcm16k(bytes: Uint8Array): Promise<Int16Array | null> {
  try {
    const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext; OfflineAudioContext?: typeof OfflineAudioContext; webkitOfflineAudioContext?: typeof OfflineAudioContext };
    const Ctx = w.AudioContext ?? w.webkitAudioContext;
    const Offline = w.OfflineAudioContext ?? w.webkitOfflineAudioContext;
    if (!Ctx || !Offline) return null;
    const ctx = new Ctx();
    let buffer: AudioBuffer;
    try {
      buffer = await ctx.decodeAudioData(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer);
    } finally {
      void ctx.close?.();
    }
    if (!(buffer.duration > 0)) return null;
    const off = new Offline(1, Math.max(1, Math.ceil(buffer.duration * 16000)), 16000);
    const src = off.createBufferSource();
    src.buffer = buffer;
    src.connect(off.destination); // one channel out: a stereo recording is mixed down
    src.start();
    const rendered = await off.startRendering();
    return floatToPcm16(rendered.getChannelData(0));
  } catch {
    return null;
  }
}
